<template>
    <div v-if="vineyard" class="container">
      <div class="row">
          <div class="col-12 title py-3">
              <div class="d-flex justify-content-between align-items-center">
                  <h2 class="fw-bold text-center m-0">{{ vineyard.name }}</h2>
                  <a @click="$router.back()" class="backBtn cursor-pointer">{{ $t("goBack")}}</a> 
              </div>
          </div>
          <hr>
      </div>
      <div class="slider-section" v-if="!isFullscreen">
        <h2 class="slider-section-title">{{ $t("vineyards.ourWines") }}</h2>
        <div class="slider-row">
          <button v-if="bottles && bottles.length > 4 || isTablet && bottles && bottles.length > 2 || isMobile && bottles && bottles.length > 1" class="custom-arrow-btn custom-arrow-360" @click="bottlePrev">
            <span class="arrow-circle-bg">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#ddb427"/><path fill="none" stroke="#222" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M9.5 12L5.5 8l4-4"/></svg>
            </span>
          </button>
          <div class="slider-content">
            <vueper-slides
              ref="bottleSlider"
              class="bottle-slider"
              :visible-slides="4"
              :slide-ratio="0.38"
              :arrows="false"
              :bullets="false"
              fixed-height="350px"
              :touchable="false"
              :gap="2"
              slide-multiple
              :breakpoints="{
                1199: { visibleSlides: 2, slideMultiple: 2, slideRatio: 0.7, touchable: true },
                767: { visibleSlides: 1, slideMultiple: 1, slideRatio: 1, touchable: true }
              }"
            >
              <vueper-slide v-for="(bottle, idx) in bottles" :key="idx">
                <template v-slot:content>
                  <div class="bottle-slide-card">
                    <img :src="bottle.image" class="bottle-img-large" />
                    <div class="bottle-name">{{ bottle.name }}</div>
                  </div>
                </template>
              </vueper-slide>
            </vueper-slides>
          </div>
          <button v-if="bottles && bottles.length > 4 || isTablet && bottles && bottles.length > 2 || isMobile && bottles && bottles.length > 1" class="custom-arrow-btn custom-arrow-360" @click="bottleNext">
            <span class="arrow-circle-bg">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#ddb427"/><path fill="none" stroke="#222" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M6.5 4l4 4-4 4"/></svg>
            </span>
          </button>
        </div>
      </div>
  
      <div class="slider-section">
        <h2 class="slider-section-title">{{ $t("vineyards.360View") }}</h2>
        <div class="slider-row">
          <button v-if="images360 && images360.length > 1" class="custom-arrow-btn custom-arrow-360" @click="prev360">
            <span class="arrow-circle-bg">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#ddb427"/><path fill="none" stroke="#222" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M9.5 12L5.5 8l4-4"/></svg>
            </span>
          </button>
          <div class="slider-content">
            <div class="viewer" ref="viewer"></div>
          </div>
          <button v-if="images360 && images360.length > 1" class="custom-arrow-btn custom-arrow-360" @click="next360">
            <span class="arrow-circle-bg">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#ddb427"/><path fill="none" stroke="#222" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M6.5 4l4 4-4 4"/></svg>
            </span>
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <p>Vineyard not found.</p>
    </div>
  </template>
  
  <script>
  import { Viewer } from 'photo-sphere-viewer';
  import 'photo-sphere-viewer/dist/photo-sphere-viewer.css';
  import { VueperSlides, VueperSlide } from "vueperslides";
  import "vueperslides/dist/vueperslides.css";
  import vineyardList from '@/data/vineyards.json';
  
  export default {
    name: 'VineyardDetails',
    components: { VueperSlides, VueperSlide },
    data() {
      return {
        vineyard: null,
        bottles: [],
        images360: [],
        current360: 0,
        viewerInstance: null,
        windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
        vineyardList: vineyardList,
        viewers: [],
        activeViewer: null,
        isFullscreen: false,
      }
    },
    computed: {
      isMobile() {
        return this.windowWidth <= 768;
      },
      isTablet() {
        return this.windowWidth <= 1024 && this.windowWidth >= 768;
      }
    },
    mounted() {
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('fullscreenchange', this.handleFullscreenResize);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenResize);
      document.addEventListener('mozfullscreenchange', this.handleFullscreenResize);
      document.addEventListener('MSFullscreenChange', this.handleFullscreenResize);

      this.vineyard = vineyardList.find(v => v.slug === this.$route.params.slug);
      if (this.vineyard) {
        this.bottles = this.vineyard.bottles;
        this.images360 = this.vineyard.images360;
        this.initViewer();
      }
    },
    beforeDestroy() {
      if (this.viewerInstance) this.viewerInstance.destroy();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('fullscreenchange', this.handleFullscreenResize);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenResize);
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenResize);
      document.removeEventListener('MSFullscreenChange', this.handleFullscreenResize);
    },
    methods: {
      handleResize() {
        this.windowWidth = window.innerWidth;
      },
      handleFullscreenResize() {
        const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        this.isFullscreen = !!isFullscreen;
        if (this.viewerInstance && typeof this.viewerInstance.resize === 'function') {
          this.viewerInstance.resize();
        }
      },
      initViewer() {
        this.$nextTick(() => {
          if (this.viewerInstance) this.viewerInstance.destroy();
          this.viewerInstance = new Viewer({
            container: this.$refs.viewer,
            panorama: this.images360[this.current360],
            navbar: true,
            defaultLong: '0deg',
            defaultLat: '0deg',
            defaultZoomLvl: 0,
            size: { width: this.isMobile ? '100vw' : '100%', height: this.isMobile ? 350 : 500 }
          });
        });
      },
      next360() {
        if (!this.images360.length) return;
        this.current360 = (this.current360 + 1) % this.images360.length;
        this.initViewer();
      },
      prev360() {
        if (!this.images360.length) return;
        this.current360 = (this.current360 - 1 + this.images360.length) % this.images360.length;
        this.initViewer();
      },
      bottlePrev() {
        this.$refs.bottleSlider && this.$refs.bottleSlider.previous();
      },
      bottleNext() {
        this.$refs.bottleSlider && this.$refs.bottleSlider.next();
      }
    }
  }
  </script>
  
  <style scoped>
  .title h2 {
      margin: 0;
      font-weight: 700;
  }
  .backBtn{
    text-decoration: none;
    color: black;
    padding: 0.5rem;
    align-self: center;
  }
  hr {
    opacity: 1 !important;
    background-color: transparent;
    border: 1px solid black !important;
    margin-bottom: 1rem;
  }
  .vineyard-info {
    text-align: center;
    margin-bottom: 2rem;
    color: #444;
  }
  .slider-section {
    border-radius: 18px;
    box-shadow: none;
    padding: 1.5rem 2rem 2.2rem 2rem;
    margin-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
    position: relative;
  }
  .slider-section-title {
    text-align: center;
    font-size: 1.4rem;
    font-weight: 700;
    color: #bfa14a;
    margin-bottom: 1.2rem;
    letter-spacing: 0.04em;
  }
  .slider-row {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }
  .slider-content {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  }
  .bottle-slider {
    --vs-arrow-bg: #ddb427;
    --vs-arrow-color: #222;
    --vs-bullet-active-bg: #ddb427;
    --vs-bullet-bg: #eee;
    margin: 0 auto;
    width: 100%;
  }
  .bottle-slide-card {
    background: linear-gradient(135deg, #fff 70%, #f8f8f8 100%);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
    padding: 1.5rem 1.2rem 2rem 1.2rem;
    transition: box-shadow 0.2s;
    border: 1.5px solid #f0e6d6;
  }
  .bottle-slide-card:hover {
    box-shadow: 0 8px 32px rgba(0,0,0,0.16);
    border-color: #ddb427;
  }
  .bottle-img-large {
    max-height: 260px;
    max-width: 100%;
    object-fit: contain;
    display: block;
    margin: 0 auto 0.7rem auto;
  }
  .bottle-name {
    font-size: .75rem;
    font-weight: 600;
    color: #222;
    text-align: center;
    margin-top: 0.2rem;
    letter-spacing: 0.01em;
  }
  .vueperslides__arrow {
    background: #ddb427 !important;
    color: #222 !important;
    border-radius: 50% !important;
    width: 36px !important;
    height: 36px !important;
    font-size: 1.3rem !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.10);
    top: 50% !important;
    transform: translateY(-50%) !important;
  }
  .vueperslides__arrow--prev {
    left: 0 !important;
  }
  .vueperslides__arrow--next {
    right: 0 !important;
  }
  .vueperslides__bullets {
    margin-top: 0.2rem !important;
    bottom: -18px !important;
  }
  .vueperslides__bullet {
    background: #eee !important;
    border: none !important;
    width: 8px !important;
    height: 8px !important;
    margin: 0 2px !important;
  }
  .vueperslides__bullet--active {
    background: #ddb427 !important;
  }
  .viewer {
    width: 600px;
    max-width: 90vw;
    height: 400px;
    border-radius: 18px;
    overflow: hidden;
    background: #e0e0e0;
    z-index: 9999;
  }
  .custom-arrow-btn {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    max-width: 36px;
    max-height: 36px;
    background: none;
    border: none;
    padding: 0;
    margin: 0 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: none;
    outline: none;
    transition: filter 0.15s;
    align-self: center;
  }
  .custom-arrow-btn:active,
  .custom-arrow-btn:focus {
    filter: brightness(0.95);
  }
  .custom-arrow-btn svg {
    width: 36px;
    height: 36px;
    display: block;
  }
  .bottle-slider /deep/ .vueperslides__parallax-wrapper:before,
  .bottle-slider /deep/ .vueperslides__parallax-wrapper:after {
    box-shadow: none !important;
    content: none !important;
  }
  @media (max-width: 768px) {
    .bottle-img-large {
      max-height: 305px;
    }
    .bottle-slide-card {
      padding: 0.5rem 0.1rem 0.7rem 0.1rem;
      border-radius: 10px;
    }
    .slider-section {
      padding: 0.7rem 0.1rem 0.8rem 0.1rem;
      max-width: 100vw;
      margin-bottom: 1.2rem;
    }
    .slider-section-title {
      font-size: 1.1rem;
      margin-bottom: 0.7rem;
    }
    .viewer {
      width: 100vw;
      min-width: 0;
      height: 220px;
      border-radius: 10px;
    }
    .slider-row {
      padding: 0;
      margin: 0;
    }
    .bottle-name {
      font-size: 0.7rem;
      margin-top: 0.1rem;
    }
    .custom-arrow-360 {
      display: flex !important;
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      min-height: 28px !important;
      max-width: 28px !important;
      max-height: 28px !important;
      padding: 0 !important;
      margin: 0 4px !important;
    }
    .custom-arrow-360 svg {
      width: 28px !important;
      height: 28px !important;
    }
  }
  .arrow-circle-bg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
  .custom-arrow-btn svg {
    display: block;
    width: 100%;
    height: 100%;
  }
  </style>